import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './App.css';
import { fetchAllSongs, saveClientRequest, getVendorRequests } from './api';

interface Song {
  id: string;
  name: string;
  artist: string;
  youtubeId: string;
}

interface SongSection {
  title: string;
  songs: Song[];
}

// Generate unique IDs
const generateId = () => Math.random().toString(36).substr(2, 9);

// Start with empty requests - clients will build their own list
const initialQuartetRequests: SongSection[] = [
  { title: 'Ceremony - Prelude', songs: [] },
  { title: 'Ceremony - Processional', songs: [] },
  { title: 'Ceremony - Bride Entrance', songs: [] },
  { title: 'Ceremony - Recessional', songs: [] },
  { title: 'Cocktail Hour', songs: [] }
];

// Cache keys for localStorage
const SONGS_CACHE_KEY = 'prelude_available_songs';
const SONGS_CACHE_TIMESTAMP_KEY = 'prelude_songs_cache_timestamp';
const CLIENT_REQUESTS_KEY = 'prelude_client_requests';
const CLIENT_REQUESTS_EMAIL_KEY = 'prelude_client_requests_email';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

// Helper functions for caching
const getCachedSongs = (): SongSection[] | null => {
  try {
    const cached = localStorage.getItem(SONGS_CACHE_KEY);
    const timestamp = localStorage.getItem(SONGS_CACHE_TIMESTAMP_KEY);
    if (cached && timestamp) {
      const cacheAge = Date.now() - parseInt(timestamp, 10);
      if (cacheAge < CACHE_DURATION_MS) {
        return JSON.parse(cached);
      }
    }
  } catch (error) {
    console.error('Error reading cache:', error);
  }
  return null;
};

const setCachedSongs = (songs: SongSection[]): void => {
  try {
    localStorage.setItem(SONGS_CACHE_KEY, JSON.stringify(songs));
    localStorage.setItem(SONGS_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error writing cache:', error);
  }
};

const isCacheStale = (): boolean => {
  const timestamp = localStorage.getItem(SONGS_CACHE_TIMESTAMP_KEY);
  if (!timestamp) return true;
  const cacheAge = Date.now() - parseInt(timestamp, 10);
  return cacheAge > CACHE_DURATION_MS / 2; // Refresh if older than 12 hours
};

// Helper functions for client requests persistence
const getClientRequestsFromStorage = (): SongSection[] | null => {
  try {
    const currentEmail = localStorage.getItem('prelude_user_email');
    const cachedEmail = localStorage.getItem(CLIENT_REQUESTS_EMAIL_KEY);

    // If user changed, don't use old cache
    if (currentEmail && cachedEmail && currentEmail !== cachedEmail) {
      console.log('User changed, clearing old request cache');
      localStorage.removeItem(CLIENT_REQUESTS_KEY);
      localStorage.removeItem(CLIENT_REQUESTS_EMAIL_KEY);
      return null;
    }

    const cached = localStorage.getItem(CLIENT_REQUESTS_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.error('Error reading client requests from storage:', error);
  }
  return null;
};

const saveClientRequestsToStorage = (requests: SongSection[]): void => {
  try {
    const currentEmail = localStorage.getItem('prelude_user_email');
    localStorage.setItem(CLIENT_REQUESTS_KEY, JSON.stringify(requests));
    if (currentEmail) {
      localStorage.setItem(CLIENT_REQUESTS_EMAIL_KEY, currentEmail);
    }
  } catch (error) {
    console.error('Error saving client requests to storage:', error);
  }
};

// No hardcoded songs - we load from backend/cache only

function App() {
  const navigate = useNavigate();
  const [currentVideo, setCurrentVideo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'requests' | 'available'>('requests');
  const [quartetRequests, setQuartetRequests] = useState<SongSection[]>(() => {
    const saved = getClientRequestsFromStorage();
    return saved || initialQuartetRequests;
  });
  const [editingSong, setEditingSong] = useState<string | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [draggedSong, setDraggedSong] = useState<{ sectionIndex: number; songIndex: number } | null>(null);
  const [slideshowActive, setSlideshowActive] = useState(false);
  const [slideshowIndex, setSlideshowIndex] = useState(0);
  const [availableSongsState, setAvailableSongsState] = useState<SongSection[]>(() => getCachedSongs() || []);
  const [isLoadingSongs, setIsLoadingSongs] = useState(() => !getCachedSongs());
  const [songsError, setSongsError] = useState<string | null>(null);
  const [vendorName] = useState<string>(() => {
    return localStorage.getItem('prelude_vendor_name') || 'Prelude Music';
  });
  const [showSectionSelector, setShowSectionSelector] = useState<Song | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [addedSongId, setAddedSongId] = useState<string | null>(null);
  const [showAuthRequired, setShowAuthRequired] = useState(false);

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const slideshowTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if user is authenticated
  useEffect(() => {
    const userEmail = localStorage.getItem('prelude_user_email');
    const userType = localStorage.getItem('prelude_user_type');

    if (!userEmail || userType !== 'client') {
      setShowAuthRequired(true);
    }
  }, []);

  // Check if user changed and reset state if needed
  useEffect(() => {
    const currentEmail = localStorage.getItem('prelude_user_email');
    const cachedEmail = localStorage.getItem(CLIENT_REQUESTS_EMAIL_KEY);

    if (currentEmail && cachedEmail && currentEmail !== cachedEmail) {
      console.log('User changed from', cachedEmail, 'to', currentEmail, '- resetting state');
      setQuartetRequests(initialQuartetRequests);
      localStorage.removeItem(CLIENT_REQUESTS_KEY);
      localStorage.removeItem(CLIENT_REQUESTS_EMAIL_KEY);
    }
  }, []);

  // Save client requests to localStorage whenever they change
  useEffect(() => {
    saveClientRequestsToStorage(quartetRequests);
  }, [quartetRequests]);

  // Load client's submitted requests from backend
  useEffect(() => {
    const loadClientRequests = async () => {
      const clientEmail = localStorage.getItem('prelude_user_email');
      const vendorId = localStorage.getItem('prelude_vendor_id') || 'default';

      if (!clientEmail) {
        console.log('No client email found in localStorage');
        return;
      }

      console.log('Fetching requests for client:', clientEmail, 'vendorId:', vendorId);

      try {
        const requests = await getVendorRequests(vendorId);
        console.log('All requests from backend:', requests);

        // Find requests for this client
        const clientRequests = requests.filter(
          (req: any) => req.clientEmail === clientEmail
        );
        console.log('Requests for this client:', clientRequests);

        if (clientRequests.length > 0) {
          // Get the most recent request
          const latestRequest = clientRequests[0];
          console.log('Latest request:', latestRequest);
          console.log('Request data string:', latestRequest.requestData);

          try {
            const parsedData = JSON.parse(latestRequest.requestData);
            console.log('Parsed request data:', parsedData);

            // Add IDs to songs if they don't have them
            const dataWithIds = parsedData.map((section: any) => ({
              title: section.title,
              songs: section.songs.map((song: any) => ({
                ...song,
                id: song.id || generateId()
              }))
            }));

            setQuartetRequests(dataWithIds);
            console.log('Loaded client requests from backend:', dataWithIds);
          } catch (parseError) {
            console.error('Error parsing request data:', parseError);
          }
        } else {
          console.log('No requests found for this client');
        }
      } catch (error) {
        console.error('Error loading client requests:', error);
      }
    };

    loadClientRequests();
  }, []);

  // Fetch available songs from backend on mount
  useEffect(() => {
    const loadSongs = async () => {
      const cachedSongs = getCachedSongs();

      // If we have cached songs and cache is fresh, use them but refresh in background if stale
      if (cachedSongs && cachedSongs.length > 0) {
        setAvailableSongsState(cachedSongs);
        setIsLoadingSongs(false);

        // Background refresh if cache is getting stale (older than 12 hours)
        if (isCacheStale()) {
          console.log('Cache is stale, refreshing in background...');
          try {
            const songs = await fetchAllSongs('default');
            if (songs.length > 0) {
              setAvailableSongsState(songs);
              setCachedSongs(songs);
              console.log('Songs refreshed from backend');
            }
          } catch (error) {
            console.error('Background refresh failed:', error);
            // Silent fail - we already have cached data
          }
        }
      } else {
        // No cache - must fetch from backend
        setIsLoadingSongs(true);
        setSongsError(null);
        try {
          const songs = await fetchAllSongs('default');
          if (songs.length > 0) {
            setAvailableSongsState(songs);
            setCachedSongs(songs);
          } else {
            setSongsError('No songs available from server. Please try again later.');
          }
        } catch (error) {
          console.error('Failed to load songs from backend:', error);
          setSongsError('Failed to load songs from server. Please check your connection and try again.');
        } finally {
          setIsLoadingSongs(false);
        }
      }
    };
    loadSongs();
  }, []);

  // Slideshow effect
  useEffect(() => {
    if (slideshowActive) {
      // Get all songs inline to satisfy eslint dependency check
      const allSongs: Song[] = [];
      quartetRequests.forEach(section => {
        section.songs.forEach(song => allSongs.push(song));
      });

      if (allSongs.length === 0) {
        setSlideshowActive(false);
        return;
      }

      setCurrentVideo(allSongs[slideshowIndex].youtubeId);

      slideshowTimeoutRef.current = setTimeout(() => {
        const nextIndex = (slideshowIndex + 1) % allSongs.length;
        setSlideshowIndex(nextIndex);
      }, 10000); // 10 seconds per song

      return () => {
        if (slideshowTimeoutRef.current) {
          clearTimeout(slideshowTimeoutRef.current);
        }
      };
    }
  }, [slideshowActive, slideshowIndex, quartetRequests]);

  const handleMouseEnter = (youtubeId: string) => {
    if (slideshowActive) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setCurrentVideo(youtubeId);
    }, 300);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleStop = () => {
    if (slideshowActive) {
      setSlideshowActive(false);
      setSlideshowIndex(0);
    }
    setCurrentVideo(null);
  };

  const handleRemoveSong = (sectionIndex: number, songId: string) => {
    setQuartetRequests(prev => {
      const newSections = [...prev];
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        songs: newSections[sectionIndex].songs.filter(s => s.id !== songId)
      };
      return newSections;
    });
  };

  const handleEditUrl = (songId: string, currentUrl: string) => {
    setEditingSong(songId);
    setEditUrl(currentUrl);
  };

  const handleSaveUrl = (sectionIndex: number, songId: string) => {
    setQuartetRequests(prev => {
      const newSections = [...prev];
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        songs: newSections[sectionIndex].songs.map(s =>
          s.id === songId ? { ...s, youtubeId: editUrl } : s
        )
      };
      return newSections;
    });
    setEditingSong(null);
    setEditUrl('');
  };

  const handleDragStart = (sectionIndex: number, songIndex: number) => {
    setDraggedSong({ sectionIndex, songIndex });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetSectionIndex: number, targetSongIndex: number) => {
    if (!draggedSong) return;

    setQuartetRequests(prev => {
      const newSections = [...prev];
      const sourceSong = newSections[draggedSong.sectionIndex].songs[draggedSong.songIndex];

      // Remove from source
      newSections[draggedSong.sectionIndex] = {
        ...newSections[draggedSong.sectionIndex],
        songs: newSections[draggedSong.sectionIndex].songs.filter((_, i) => i !== draggedSong.songIndex)
      };

      // Add to target
      const targetSongs = [...newSections[targetSectionIndex].songs];
      targetSongs.splice(targetSongIndex, 0, sourceSong);
      newSections[targetSectionIndex] = {
        ...newSections[targetSectionIndex],
        songs: targetSongs
      };

      return newSections;
    });

    setDraggedSong(null);
  };

  const handleAddToRequests = (song: Song) => {
    setShowSectionSelector(song);
  };

  const handleAddToSection = (sectionIndex: number) => {
    if (!showSectionSelector) return;

    const songId = showSectionSelector.id;

    setQuartetRequests(prev => {
      const newSections = [...prev];
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        songs: [...newSections[sectionIndex].songs, { ...showSectionSelector, id: generateId() }]
      };
      return newSections;
    });

    // Show the "Added!" popup
    setAddedSongId(songId);
    setTimeout(() => {
      setAddedSongId(null);
    }, 1500);

    setShowSectionSelector(null);
  };

  const toggleSlideshow = () => {
    if (slideshowActive) {
      setSlideshowActive(false);
      setSlideshowIndex(0);
      setCurrentVideo(null);
    } else {
      setSlideshowIndex(0);
      setSlideshowActive(true);
    }
  };

  const handleSubmitRequest = async () => {
    const clientEmail = localStorage.getItem('prelude_user_email') || '';
    const clientName = localStorage.getItem('prelude_client_name') || clientEmail;
    const vendorId = localStorage.getItem('prelude_vendor_id') || 'default';

    if (!clientEmail) {
      setSubmitError('Please sign in to submit your request');
      return;
    }

    // Check if there are any songs selected
    const totalSongs = quartetRequests.reduce((total, section) => total + section.songs.length, 0);
    if (totalSongs === 0) {
      setSubmitError('Please add at least one song to your request');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const result = await saveClientRequest(vendorId, clientName, clientEmail, quartetRequests);
      if (result.success) {
        setSubmitSuccess(true);
        setSubmitError(null);
        // Clear the request after successful submission
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } else {
        setSubmitError('Failed to submit request. Please try again.');
      }
    } catch (error) {
      setSubmitError('Error submitting request. Please check your connection.');
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };


  const renderRequestsList = (sections: SongSection[]) => (
    <div className="sections-container">
      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex} className="section">
          <h3 className="section-title">{section.title}</h3>
          <div className="song-list">
            {section.songs.map((song, songIndex) => (
              <div
                key={song.id}
                className="song-item"
                draggable
                onDragStart={() => handleDragStart(sectionIndex, songIndex)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(sectionIndex, songIndex)}
              >
                <div className="drag-handle">⋮⋮</div>
                <div className="song-info">
                  <span className="song-name">{song.name}</span>
                  <span className="song-artist">{song.artist}</span>
                  {editingSong === song.id && (
                    <div className="edit-url">
                      <input
                        type="text"
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        placeholder="YouTube Video ID"
                      />
                      <button onClick={() => handleSaveUrl(sectionIndex, song.id)}>Save</button>
                      <button onClick={() => setEditingSong(null)}>Cancel</button>
                    </div>
                  )}
                </div>
                <div className="song-actions">
                  {editingSong !== song.id && (
                    <button
                      className="edit-button"
                      onClick={() => handleEditUrl(song.id, song.youtubeId)}
                    >
                      ✎
                    </button>
                  )}
                  <button
                    className={`play-button ${currentVideo === song.youtubeId ? 'playing' : ''}`}
                    onMouseEnter={() => handleMouseEnter(song.youtubeId)}
                    onMouseLeave={handleMouseLeave}
                    onClick={handleStop}
                  >
                    {currentVideo === song.youtubeId ? '⏹' : '▶'}
                  </button>
                  <button
                    className="remove-button"
                    onClick={() => handleRemoveSong(sectionIndex, song.id)}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderAvailableList = (sections: SongSection[]) => (
    <div className="sections-container">
      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex} className="section">
          <h3 className="section-title">{section.title}</h3>
          <div className="song-list">
            {section.songs.map((song, songIndex) => (
              <div key={song.id} className="song-item">
                <div className="song-info">
                  <span className="song-name">{song.name}</span>
                  <span className="song-artist">{song.artist}</span>
                </div>
                <div className="song-actions">
                  <button
                    className={`play-button ${currentVideo === song.youtubeId ? 'playing' : ''}`}
                    onMouseEnter={() => handleMouseEnter(song.youtubeId)}
                    onMouseLeave={handleMouseLeave}
                    onClick={handleStop}
                  >
                    {currentVideo === song.youtubeId ? '⏹' : '▶'}
                  </button>
                  <div className="add-button-container">
                    <button
                      className="add-button"
                      onClick={() => handleAddToRequests(song)}
                    >
                      +
                    </button>
                    {addedSongId === song.id && (
                      <div className="added-popup">Added!</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="App">
      <header className="site-header">
        <div className="header-content">
          <h1 className="site-title">Prelude Song Player</h1>
          <div className="header-links">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/portal" className="nav-link">Portal</Link>
          </div>
        </div>
      </header>

      <div className="app-layout">
        <div className="video-player-container">
          <div className="vendor-name-display">{vendorName}</div>
          <div className="video-player-fixed">
            {currentVideo ? (
              <iframe
                key={currentVideo}
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${currentVideo}?autoplay=1`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : (
              <div className="video-placeholder">
                Hover over a play button to preview
              </div>
            )}
          </div>
        </div>

        <div className="main-content">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
              onClick={() => setActiveTab('requests')}
            >
              My Requests
            </button>
            <button
              className={`tab ${activeTab === 'available' ? 'active' : ''}`}
              onClick={() => setActiveTab('available')}
            >
              Available Songs
            </button>
          </div>

          {activeTab === 'requests' && (
            <div className="slideshow-controls">
              <button
                className={`slideshow-button ${slideshowActive ? 'active' : ''}`}
                onClick={toggleSlideshow}
              >
                {slideshowActive ? '⏹ Stop Slideshow' : '▶ Start Slideshow (10s each)'}
              </button>
              <button
                className="submit-request-button"
                onClick={handleSubmitRequest}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request to Vendor'}
              </button>
            </div>
          )}

          <div className="tab-content">
            {submitSuccess && (
              <div className="success-message">
                Your request has been submitted successfully! Your vendor will review it shortly.
              </div>
            )}
            {submitError && <div className="error-message">{submitError}</div>}
            {songsError && <div className="error-message">{songsError}</div>}
            {activeTab === 'requests'
              ? renderRequestsList(quartetRequests)
              : isLoadingSongs
                ? <div className="loading-message">Loading songs from server...</div>
                : renderAvailableList(availableSongsState)
            }
          </div>
        </div>
      </div>

      {showSectionSelector && (
        <div className="section-selector-overlay">
          <div className="section-selector-modal">
            <h3>Add to which section?</h3>
            <p className="selected-song-info">
              {showSectionSelector.name} by {showSectionSelector.artist}
            </p>
            <div className="section-options">
              {quartetRequests.map((section, index) => (
                <button
                  key={index}
                  className="section-option"
                  onClick={() => handleAddToSection(index)}
                >
                  {section.title}
                </button>
              ))}
            </div>
            <button
              className="cancel-button"
              onClick={() => setShowSectionSelector(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showAuthRequired && (
        <div className="auth-required-overlay">
          <div className="auth-required-modal">
            <h3>Sign In Required</h3>
            <p>You need to sign in as a client to access this page.</p>
            <button
              className="go-to-portal-button"
              onClick={() => navigate('/portal')}
            >
              Go to Portal →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
